import { Tooltip } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown'
import { EmptyView } from '@/components/EmptyView.tsx'
import {
  AVAILABLE_CUSTOM_IMAGE_STATUSES,
  MACHINE_ATTR_TAG_OPTIONS,
  MachineAttrTag,
} from '@/consts/image'
import { GhostType, MachineType } from '@/consts/machine'
import { Image } from '@/models/image'
import { Machine } from '@/models/machine'
import {
  buildBaseResourceDesc,
  getBootSourceCompatibility,
  getMinimumInstanceCountForBootSource,
} from '@/pages/create-instance/utils/extraStorage'
import { fetchAllPages } from '@/utils/pagination'
import { cn } from '@/utils/style'

import { BOOT_SOURCE_PAGE_SIZE, BootSourcePagination } from './boot-source-pagination'
import { BootSourceSkeleton } from './boot-source-skeleton'
import { BootSourceCompatibilityMessage } from './BootSourceCompatibilityMessage'
import { MINIMUM_RESOURCE_TOOLTIP_PROPS, MinimumResourceBadge } from './minimum-resource-badge'
import { SelectMachineWarning } from './select-machine-warning'

interface ImageSelectorProps {
  machine?: Machine
  type: MachineType
  value?: Image
  count?: number
  onChange: (value?: Image) => void
  onImagesChange?: (value: Image[]) => void
  ghostType?: GhostType
  emptyMessageVariant?: 'default' | 'team'
  enforceResourceLimit?: boolean
  supportsExtraResources?: boolean
  searchTerm?: string
}

export function ImageSelector({
  value,
  machine,
  count,
  onChange,
  onImagesChange,
  ghostType,
  emptyMessageVariant = 'default',
  enforceResourceLimit = false,
  supportsExtraResources = false,
  searchTerm = '',
}: ImageSelectorProps) {
  const [loading, setLoading] = useState(false)
  const [showSkeleton, setShowSkeleton] = useState(false)
  const [images, setImages] = useState<Image[]>([])
  const [page, setPage] = useState(1)
  const [selectedTag, setSelectedTag] = useState<string | number>('all')
  const prevMachineKeyRef = useRef<string | undefined>(machine?.key)
  const prevGhostTypeRef = useRef<GhostType | undefined>()
  const loadedContextKeyRef = useRef<string>()
  const requestIdRef = useRef(0)
  const { t } = useTranslation()
  const isSubscriptionResourceMode = enforceResourceLimit && !supportsExtraResources
  const imageContextKey = `${machine?.key ?? ''}:${ghostType ?? ''}:${selectedTag}`
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase()
  const getImages = useCallback(async () => {
    const requestId = ++requestIdRef.current
    const isInitialContextLoad = loadedContextKeyRef.current !== imageContextKey

    setLoading(true)
    setShowSkeleton(isInitialContextLoad)
    if (!machine) {
      setLoading(false)
      setShowSkeleton(false)
      return
    }

    // Calculate machine_attr_tag for Official Images filter
    let machineAttrTag: number | undefined
    if (ghostType === GhostType.Image && selectedTag !== 'all') {
      machineAttrTag = typeof selectedTag === 'number' ? selectedTag : undefined
    }

    try {
      const serverImages = await fetchAllPages({
        fetchPage: (page, pageSize) =>
          API.getImageList({
            agent_id: machine.agent_id ?? 0,
            machine_category: machine.machineCategory,
            resource_desc: buildBaseResourceDesc(machine, count ?? 1),
            page,
            per_page: pageSize,
            machine_attr_tag: machineAttrTag,
          }),
        getItems: (response) => response.images,
      })

      if (requestId !== requestIdRef.current) return

      let filteredImages = serverImages.filter(
        (im) =>
          machine.unitSpec?.gpuName?.includes('H100') ||
          (im.resID !== 'img-5yp6v8l8' && im.resID !== 'img-y4lvnxqw'),
      )

      // Filter by GhostType
      if (ghostType === GhostType.Image) {
        // Official images (public = true)
        filteredImages = filteredImages.filter((im) => im.public === true)
      } else if (ghostType === GhostType.CustomImage) {
        // Custom images (public = false and available status)
        filteredImages = filteredImages.filter(
          (im) => !im.public && AVAILABLE_CUSTOM_IMAGE_STATUSES.includes(im.storeStatus),
        )
      }

      const nextImages = filteredImages.map((im) => new Image(im))
      setImages(nextImages)
      onImagesChange?.(nextImages)
      loadedContextKeyRef.current = imageContextKey
    } catch (error) {
      if (requestId !== requestIdRef.current) return
      console.error('Failed to fetch complete image list:', error)
      setImages([])
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false)
        setShowSkeleton(false)
      }
    }
  }, [machine, count, ghostType, selectedTag, onImagesChange, imageContextKey])

  const filteredImages = useMemo(() => {
    if (!normalizedSearchTerm) return images

    return images.filter((image) =>
      [image.id, image.resID, image.alias, image.description].some((value) =>
        String(value).toLocaleLowerCase().includes(normalizedSearchTerm),
      ),
    )
  }, [images, normalizedSearchTerm])

  const visibleImages = useMemo(() => {
    const start = (page - 1) * BOOT_SOURCE_PAGE_SIZE
    return filteredImages.slice(start, start + BOOT_SOURCE_PAGE_SIZE)
  }, [filteredImages, page])

  useEffect(() => {
    setPage(1)
  }, [machine?.key, ghostType, selectedTag, normalizedSearchTerm])

  useEffect(() => {
    if (normalizedSearchTerm) return

    const selectedIndex = images.findIndex((image) => image.id === value?.id)
    if (selectedIndex >= 0) {
      setPage(Math.floor(selectedIndex / BOOT_SOURCE_PAGE_SIZE) + 1)
    }
  }, [images, normalizedSearchTerm, value?.id])

  useEffect(() => {
    if (machine) {
      getImages()

      // Clear selection when machine or ghostType changes
      const machineChanged = prevMachineKeyRef.current !== machine.key
      const ghostTypeChanged =
        prevGhostTypeRef.current !== undefined && prevGhostTypeRef.current !== ghostType

      if ((prevMachineKeyRef.current !== undefined && machineChanged) || ghostTypeChanged) {
        onChange(undefined)
      }
    }

    prevMachineKeyRef.current = machine?.key
    prevGhostTypeRef.current = ghostType
  }, [machine, ghostType, getImages])

  if (!machine) {
    return <SelectMachineWarning />
  }

  return (
    <section
      className="bg-panel-white rounded-2xl"
      aria-busy={loading}
      data-ai-component="image-selector"
      data-ai-state={value ? 'selected' : 'needs-selection'}
      data-ai-value={value?.resID ?? ''}
    >
      {ghostType === GhostType.Image && (
        <div
          className="flex items-center gap-2 mb-4"
          data-ai-component="image-type-filter"
          data-ai-action="select-image-type"
          data-ai-value={selectedTag}
        >
          <span className="text-tertiary t-xs">{t('customImage.filter.title', 'Type')}</span>
          <Dropdown
            options={MACHINE_ATTR_TAG_OPTIONS.map((opt) => ({
              value: opt.value,
              label: t(opt.labelKey),
            }))}
            value={selectedTag}
            showAllOption
            allOptionLabel={t('common.any', 'Any')}
            onChange={(v) => {
              setSelectedTag(v as string | number)
              onChange(undefined)
            }}
            rootClassName="w-44"
            className="t-x-sb h-8"
          />
        </div>
      )}
      {showSkeleton && <BootSourceSkeleton />}
      {!showSkeleton && filteredImages.length > 0 && (
        <div
          className="grid h-[244px] grid-cols-4 grid-rows-2 gap-4 overflow-hidden"
          role="radiogroup"
          aria-label={t('create.bootSourceType.image')}
        >
          {visibleImages.map((image) => {
            const minimumInstanceCount = getMinimumInstanceCountForBootSource({
              machine,
              count,
              source: image,
              supportsExtraResources,
            })
            const disabled = enforceResourceLimit && minimumInstanceCount === undefined
            const compatibility = getBootSourceCompatibility({
              machine,
              count: disabled ? machine.maxAvailUnits : count,
              source: image,
              supportsExtraResources,
            })
            const disabledReason = disabled ? (
              <BootSourceCompatibilityMessage
                compatibility={compatibility}
                sourceType="image"
                supportsExtraResources={supportsExtraResources}
              />
            ) : undefined
            const selected = value?.id === image.id

            return (
              <Tooltip
                key={image.id}
                title={disabledReason}
                {...(disabled && isSubscriptionResourceMode ? MINIMUM_RESOURCE_TOOLTIP_PROPS : {})}
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-disabled={disabled}
                  className={cn(
                    'h-full w-full overflow-hidden rounded-lg border border-neutral-200 p-4 text-left cursor-pointer',
                    {
                      'bg-highlight/10 border-secondary': selected,
                      'opacity-50 cursor-not-allowed': disabled,
                    },
                  )}
                  data-ai-component="image-option"
                  data-ai-action="select-image"
                  data-ai-state={disabled ? 'disabled' : selected ? 'selected' : 'available'}
                  data-ai-value={image.resID}
                  aria-label={`${image.resID} ${image.alias}`}
                  onClick={(event) => {
                    event.stopPropagation()
                    if (!disabled) onChange(image)
                  }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="min-w-0 flex-1 truncate t-xs text-primary">{image.resID}</div>
                    <MinimumResourceBadge resource={compatibility.required} />
                  </div>
                  <div className="t-sb mb-0.5 truncate text-primary">{image.alias}</div>
                  <p className="line-clamp-2 text-secondary t-xs">{image.description}</p>
                </button>
              </Tooltip>
            )
          })}
        </div>
      )}

      {!showSkeleton && filteredImages.length > 0 && (
        <BootSourcePagination
          current={page}
          selected={value && filteredImages.some((image) => image.id === value.id) ? 1 : 0}
          total={filteredImages.length}
          onChange={setPage}
        />
      )}

      {!showSkeleton && filteredImages.length === 0 && (
        <EmptyView
          className="h-[300px]"
          content={
            <span className="t-sm text-secondary">
              {normalizedSearchTerm && images.length > 0 ? (
                <Trans i18nKey="create.bootSourceSearchNoResults">No matching boot sources</Trans>
              ) : emptyMessageVariant === 'team' ? (
                <span className="block max-w-[760px] text-center leading-6">
                  <Trans i18nKey="customImage.filter.teamNoCompatibleImages">
                    No compatible images are currently available for this machine. Please contact
                    your administrator to configure the required compatible image.
                  </Trans>
                </span>
              ) : ghostType === GhostType.Image && selectedTag === MachineAttrTag.VirtualMachine ? (
                <Trans i18nKey="customImage.filter.vmNotSupported">
                  Container-based machine. VM Images aren&apos;t supported here. Please try another
                  machine.
                </Trans>
              ) : ghostType === GhostType.Image && selectedTag === MachineAttrTag.Container ? (
                <Trans i18nKey="customImage.filter.containerNotSupported">
                  VM-based machine. Container Images aren&apos;t supported here. Please try another
                  machine.
                </Trans>
              ) : (
                <Trans i18nKey="common.emptyViewNoDataMsg">No Data</Trans>
              )}
            </span>
          }
        />
      )}
    </section>
  )
}
